/** React */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

/** MUI icons */
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/** MUI */
import { styled } from "@mui/material/styles";
import { Box, Card, CardContent, Chip, Grid, Typography } from "@mui/material";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";

/** Component styles */
const PREFIX = "SetupSummary";

const classes = {
    cardContentRoot: `${PREFIX}-cardContentRoot`,
    summaryLabels: `${PREFIX}-summaryLabels`,
    summaryDetails: `${PREFIX}-summaryDetails`,
};

const StyledContainer = styled(Box)(({ theme }) => ({
    [`& .${classes.cardContentRoot}`]: {
        padding: "24px 30px 30px !important",
    },

    [`& .${classes.summaryLabels}`]: {
        display: "flex",
        justifyContent: "space-between",
        padding: "24px 0px 8px",
        borderBottom: "1px solid #cccc",
    },

    [`& .${classes.summaryDetails}`]: {
        display: "flex",
        justifyContent: "space-between",
        paddingLeft: "16px",
    },
}));

const Accordion = styled((props) => <MuiAccordion disableGutters elevation={0} square {...props} />)({
    boxShadow: "none",
    "&:before": {
        display: "none",
    },
});

const AccordionSummary = styled((props) => (
    <MuiAccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#031851" }} />} {...props} />
))({
    "& .MuiAccordionSummary-content": {
        margin: "12px 0",
    },
});

const AccordionDetails = styled(MuiAccordionDetails)({
    padding: "0px 0px 20px 0px",
    "& .MuiCard-root": {
        boxShadow: "none",
    },
});

export const SetupSummary = (props) => {
    /** Properties */
    const {
        valuesSetupXero,
        valuesSetupBanking,
        valuesSetupReport,
        valuesSetupTaxes,
        valuesSetupOther,
        valuesSetupCon,
    } = props;

    /** Internationalization i18n */
    const [t] = useTranslation("global");

    /** React redux */
    const todos = useSelector((state) => state.todos);

    /** Component states */
    const [expanded, setExpanded] = useState(true);

    /** Component functions */
    const handleChangeSumaryAccordion = (event, isExpanded) => {
        setExpanded(isExpanded);
    };

    return (
        <StyledContainer>
            <Card
                elevation={0}
                sx={{
                    mt: 3,
                    borderRadius: 8,
                }}
            >
                <CardContent sx={{ pt: 1 }}>
                    <Accordion
                        expanded={expanded}
                        onChange={handleChangeSumaryAccordion}
                        TransitionProps={{ unmountOnExit: true }}
                    >
                        <AccordionSummary
                            aria-controls="panel-content"
                            id="panel-header"
                            sx={{
                                borderBottom: "2px solid #ff5968",
                            }}
                        >
                            <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                                <Grid item>
                                    <Typography
                                        variant="h2"
                                        align="center"
                                        sx={{
                                            fontWeight: 700,
                                        }}
                                    >
                                        Software setup
                                    </Typography>
                                </Grid>
                                <Grid item sx={{ pr: 1 }}>
                                    <Typography variant="h3">
                                        {`$${parseFloat(
                                            (!valuesSetupBanking.chip ? 0 : valuesSetupBanking.projectTotal) +
                                                (!valuesSetupReport.chip ? 0 : valuesSetupReport.projectTotal) +
                                                (!valuesSetupXero.chip ? 0 : valuesSetupXero.projectTotal) +
                                                (!valuesSetupTaxes.chip ? 0 : valuesSetupTaxes.projectTotal) +
                                                (!valuesSetupOther.chip ? 0 : valuesSetupOther.projectTotal) +
                                                (!valuesSetupCon.chip ? 0 : valuesSetupCon.consulProjectTotal)
                                        ).toLocaleString(todos.amountFormat, {
                                            maximumFractionDigits: 2,
                                        })}/${t(`proposals.oneTime`)}`}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </AccordionSummary>
                        <AccordionDetails>
                            {valuesSetupXero.chip && (
                                <Grid container justifyContent="flex-end" spacing={2}>
                                    <Grid item xs={11}>
                                        <Box className={classes.summaryLabels}>
                                            <Typography
                                                variant="h2"
                                                align="center"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: valuesSetupXero.isError? "#ff5968": "primary"
                                                }}
                                            >
                                                Xero
                                            </Typography>
                                            <Chip
                                                id="hour-addons-chip"
                                                label={
                                                    <Typography variant="h3" align="center">
                                                        {`$${parseFloat(valuesSetupXero.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t(`proposals.oneTime`)}`}
                                                    </Typography>
                                                }
                                                sx={{
                                                    //backgroundColor: "#2f3190",
                                                    backgroundColor: valuesSetupXero.isError? "#ff5968": "#2f3190",
                                                    color: "#fff",
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item container justifyContent="flex-end" spacing={1}>
                                        <Grid item xs={11}>
                                            <Box className={classes.summaryDetails}>
                                                <Typography
                                                    variant="h3"
                                                    align="center"
                                                    sx={{
                                                        display: "block",
                                                        mt: "auto",
                                                        mb: "auto",
                                                        fontWeight: 700,
                                                        color: valuesSetupXero.isError? "#ff5968": "primary"
                                                    }}
                                                >
                                                    {`${t("proposals.basicService")} (${valuesSetupXero.hours} hrs)`}
                                                </Typography>
                                                <>
                                                    <Typography
                                                        variant="body2"
                                                        align="center"
                                                        sx={{
                                                            display: "block",
                                                            mt: "auto !important",
                                                            mb: "auto !important",
                                                            color: valuesSetupXero.isError? "#ff5968": "primary"
                                                        }}
                                                    >
                                                        {`$${parseFloat(valuesSetupXero.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t("proposals.oneTime")}`}
                                                    </Typography>
                                                </>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                            {/* {valuesSetupBanking.chip && (
                                <Grid container justifyContent="flex-end" spacing={2}>
                                    <Grid item xs={11}>
                                        <Box className={classes.summaryLabels}>
                                            <Typography
                                                variant="h2"
                                                align="center"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: valuesSetupBanking.isError? "#ff5968": "primary"
                                                }}
                                            >
                                                Kiiper Banking
                                            </Typography>
                                            <Chip
                                                id="hour-addons-chip"
                                                label={
                                                    <Typography variant="h3" align="center">
                                                        {`$${parseFloat(valuesSetupBanking.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t(`proposals.oneTime`)}`}
                                                    </Typography>
                                                }
                                                sx={{
                                                    //backgroundColor: "#2f3190",
                                                    backgroundColor: valuesSetupBanking.isError? "#ff5968": "#2f3190",
                                                    color: "#fff",
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item container justifyContent="flex-end" spacing={1}>
                                        <Grid item xs={11}>
                                            <Box className={classes.summaryDetails}>
                                                <Typography
                                                    variant="h3"
                                                    align="center"
                                                    sx={{
                                                        display: "block",
                                                        mt: "auto",
                                                        mb: "auto",
                                                        fontWeight: 700,
                                                        color: valuesSetupBanking.isError? "#ff5968": "primary"
                                                    }}
                                                >
                                                    {`${t("proposals.basicService")} (${valuesSetupBanking.hours} hrs)`}
                                                </Typography>
                                                <>
                                                    <Typography
                                                        variant="body2"
                                                        align="center"
                                                        sx={{
                                                            display: "block",
                                                            mt: "auto !important",
                                                            mb: "auto !important",
                                                            color: valuesSetupBanking.isError? "#ff5968": "primary"
                                                        }}
                                                    >
                                                        {`$${parseFloat(valuesSetupBanking.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t("proposals.oneTime")}`}
                                                    </Typography>
                                                </>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )} */}
                            {valuesSetupTaxes.chip && (
                                <Grid container justifyContent="flex-end" spacing={2}>
                                    <Grid item xs={11}>
                                        <Box className={classes.summaryLabels}>
                                            <Typography
                                                variant="h2"
                                                align="center"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: valuesSetupTaxes.isError? "#ff5968": "primary"
                                                }}
                                            >
                                                Kiiper Taxes
                                            </Typography>
                                            <Chip
                                                id="hour-addons-chip"
                                                label={
                                                    <Typography variant="h3" align="center">
                                                        {`$${parseFloat(valuesSetupTaxes.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t(`proposals.oneTime`)}`}
                                                    </Typography>
                                                }
                                                sx={{
                                                    //backgroundColor: "#2f3190",
                                                    backgroundColor: valuesSetupTaxes.isError? "#ff5968": "#2f3190",
                                                    color: "#fff",
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item container justifyContent="flex-end" spacing={1}>
                                        <Grid item xs={11}>
                                            <Box className={classes.summaryDetails}>
                                                <Typography
                                                    variant="h3"
                                                    align="center"
                                                    sx={{
                                                        display: "block",
                                                        mt: "auto",
                                                        mb: "auto",
                                                        fontWeight: 700,
                                                        color: valuesSetupTaxes.isError? "#ff5968": "primary"
                                                    }}
                                                >
                                                    {`${t("proposals.basicService")} (${valuesSetupTaxes.hours} hrs)`}
                                                </Typography>
                                                <>
                                                    <Typography
                                                        variant="body2"
                                                        align="center"
                                                        sx={{
                                                            display: "block",
                                                            mt: "auto !important",
                                                            mb: "auto !important",
                                                            color: valuesSetupTaxes.isError? "#ff5968": "primary"
                                                        }}
                                                    >
                                                        {`$${parseFloat(valuesSetupTaxes.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t("proposals.oneTime")}`}
                                                    </Typography>
                                                </>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                            {valuesSetupReport.chip && (
                                <Grid container justifyContent="flex-end" spacing={2}>
                                    <Grid item xs={11}>
                                        <Box className={classes.summaryLabels}>
                                            <Typography
                                                variant="h2"
                                                align="center"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: valuesSetupReport.isError? "#ff5968": "primary"
                                                }}
                                            >
                                                Kiiper Reporting
                                            </Typography>
                                            <Chip
                                                id="hour-addons-chip"
                                                label={
                                                    <Typography variant="h3" align="center">
                                                        {`$${parseFloat(valuesSetupReport.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t(`proposals.oneTime`)}`}
                                                    </Typography>
                                                }
                                                sx={{
                                                    //backgroundColor: "#2f3190",
                                                    backgroundColor: valuesSetupReport.isError? "#ff5968": "#2f3190",
                                                    color: "#fff",
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item container justifyContent="flex-end" spacing={1}>
                                        <Grid item xs={11}>
                                            <Box className={classes.summaryDetails}>
                                                <Typography
                                                    variant="h3"
                                                    align="center"
                                                    sx={{
                                                        display: "block",
                                                        mt: "auto",
                                                        mb: "auto",
                                                        fontWeight: 700,
                                                        color: valuesSetupReport.isError? "#ff5968": "primary"
                                                    }}
                                                >
                                                    {`${t("proposals.basicService")} (${valuesSetupReport.hours} hrs)`}
                                                </Typography>
                                                <>
                                                    <Typography
                                                        variant="body2"
                                                        align="center"
                                                        sx={{
                                                            display: "block",
                                                            mt: "auto !important",
                                                            mb: "auto !important",
                                                            color: valuesSetupReport.isError? "#ff5968": "primary"
                                                        }}
                                                    >
                                                        {`$${parseFloat(valuesSetupReport.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t("proposals.oneTime")}`}
                                                    </Typography>
                                                </>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                            {valuesSetupOther.chip && (
                                <Grid container justifyContent="flex-end" spacing={2}>
                                    <Grid item xs={11}>
                                        <Box className={classes.summaryLabels}>
                                            <Typography
                                                variant="h2"
                                                align="center"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: valuesSetupOther.isError? "#ff5968": "primary"
                                                }}
                                            >
                                                {t("services.other")}
                                            </Typography>
                                            <Chip
                                                id="hour-addons-chip"
                                                label={
                                                    <Typography variant="h3" align="center">
                                                        {`$${parseFloat(valuesSetupOther.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t(`proposals.oneTime`)}`}
                                                    </Typography>
                                                }
                                                sx={{
                                                    //backgroundColor: "#2f3190",
                                                    backgroundColor: valuesSetupOther.isError? "#ff5968": "#2f3190",
                                                    color: "#fff",
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item container justifyContent="flex-end" spacing={1}>
                                        <Grid item xs={11}>
                                            <Box className={classes.summaryDetails}>
                                                <Typography
                                                    variant="h3"
                                                    align="center"
                                                    sx={{
                                                        display: "block",
                                                        mt: "auto",
                                                        mb: "auto",
                                                        fontWeight: 700,
                                                        color: valuesSetupOther.isError? "#ff5968": "primary"
                                                    }}
                                                >
                                                    {`${t("proposals.basicService")} (${valuesSetupOther.hours} hrs)`}
                                                </Typography>
                                                <>
                                                    <Typography
                                                        variant="body2"
                                                        align="center"
                                                        sx={{
                                                            display: "block",
                                                            mt: "auto !important",
                                                            mb: "auto !important",
                                                            color: valuesSetupOther.isError? "#ff5968": "primary"
                                                        }}
                                                    >
                                                        {`$${parseFloat(valuesSetupOther.projectTotal).toLocaleString(
                                                            todos.amountFormat,
                                                            {
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}/${t("proposals.oneTime")}`}
                                                    </Typography>
                                                </>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                            {valuesSetupCon.chip && (
                                <Grid container justifyContent="flex-end" spacing={2}>
                                    <Grid item xs={11}>
                                        <Box className={classes.summaryLabels}>
                                            <Typography
                                                variant="h2"
                                                align="center"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: valuesSetupCon.isError? "#ff5968": "primary"
                                                }}
                                            >
                                                {t(`invoicing.consulting`)}
                                            </Typography>
                                            <Chip
                                                id="hour-addons-chip"
                                                label={
                                                    <Typography variant="h3" align="center">
                                                        {`$${parseFloat(
                                                            valuesSetupCon.consulProjectTotal
                                                        ).toLocaleString(todos.amountFormat, {
                                                            maximumFractionDigits: 2,
                                                        })}/${t(`proposals.oneTime`)}`}
                                                    </Typography>
                                                }
                                                sx={{
                                                    //backgroundColor: "#2f3190",
                                                    backgroundColor: valuesSetupCon.isError? "#ff5968": "#2f3190",
                                                    color: "#fff",
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item container justifyContent="flex-end" spacing={1}>
                                        <Grid item xs={11}>
                                            <Box className={classes.summaryDetails}>
                                                <Typography
                                                    variant="h3"
                                                    align="center"
                                                    sx={{
                                                        display: "block",
                                                        mt: "auto",
                                                        mb: "auto",
                                                        fontWeight: 700,
                                                        color: valuesSetupCon.isError? "#ff5968": "primary"
                                                    }}
                                                >
                                                    {`${t(`proposals.query`)} (${valuesSetupCon.consulHours} hrs)`}
                                                </Typography>
                                                <Typography variant="body2" align="center" sx={{color: valuesSetupCon.isError? "#ff5968": "primary"}}>
                                                    {`$${parseFloat(valuesSetupCon.consulProjectTotal).toLocaleString(
                                                        todos.amountFormat,
                                                        {
                                                            maximumFractionDigits: 2,
                                                        }
                                                    )}/${t(`proposals.oneTime`)}`}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                        </AccordionDetails>
                    </Accordion>
                </CardContent>
            </Card>
        </StyledContainer>
    );
};
